import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { AlertTriangle, BarChart3, Loader2, MailCheck } from "lucide-react";
import { api } from "@/lib/api";

const LABELS = {
  nudge_day3: "Day 3 trial nudge",
  nudge_day7: "Day 7 trial nudge",
  threat_alert: "Threat alert",
  weekly_report: "Weekly report",
  milestone_100: "Milestone · $100 saved",
  milestone_500: "Milestone · $500 saved",
  milestone_1000: "Milestone · $1,000 saved",
};

export const EmailAnalytics = () => {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);

  const load = () => {
    setError(false);
    setStats(null);
    api.get("/admin/email-analytics").then((r) => setStats(r.data.stats)).catch(() => setError(true));
  };

  useEffect(load, []);

  if (error)
    return (
      <div data-testid="analytics-error" className="rounded-xl bg-[#0B101A] border border-red-500/20 p-10 text-center">
        <AlertTriangle className="w-8 h-8 mx-auto mb-3 text-red-400" />
        <p className="text-sm text-slate-400 mb-4">Could not load email analytics.</p>
        <button data-testid="analytics-retry-btn" onClick={load} className="px-5 py-2 rounded-full bg-cyan-neon text-[#040914] text-sm font-semibold hover:glow-cyan transition-shadow">
          Retry
        </button>
      </div>
    );

  if (!stats)
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-6 h-6 text-cyan-neon animate-spin" />
      </div>
    );

  const totalSent = stats.reduce((a, s) => a + s.sent, 0);
  const totalConv = stats.reduce((a, s) => a + s.conversions, 0);

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="email-analytics-panel">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-xl bg-[#0B101A] border border-white/8 p-5">
          <div className="font-mono text-[10px] tracking-wider uppercase text-slate-500">Automated emails sent</div>
          <div data-testid="analytics-total-sent" className="font-display text-3xl text-white mt-1.5">{totalSent}</div>
        </div>
        <div className="rounded-xl bg-[#0B101A] border border-white/8 p-5">
          <div className="font-mono text-[10px] tracking-wider uppercase text-slate-500">Upgrades attributed</div>
          <div data-testid="analytics-total-conversions" className="font-display text-3xl text-cyan-neon mt-1.5">{totalConv}</div>
        </div>
        <div className="rounded-xl bg-[#0B101A] border border-white/8 p-5">
          <div className="font-mono text-[10px] tracking-wider uppercase text-slate-500">Overall conversion</div>
          <div data-testid="analytics-overall-rate" className="font-display text-3xl text-[#39FF14] mt-1.5">
            {totalSent ? ((totalConv / totalSent) * 100).toFixed(1) : "0.0"}%
          </div>
        </div>
      </div>

      <div className="rounded-xl bg-[#0B101A] border border-white/8 p-6">
        <div className="flex items-center gap-2.5 mb-5">
          <BarChart3 className="w-4 h-4 text-cyan-neon" />
          <h3 className="font-display text-white">Conversion by email type</h3>
          <span className="ml-auto font-mono text-[10px] tracking-wider uppercase text-slate-500">upgrades within 7 days of send</span>
        </div>
        {stats.length === 0 ? (
          <div className="py-12 text-center text-slate-500 text-sm" data-testid="analytics-empty">
            <MailCheck className="w-8 h-8 mx-auto mb-3 text-slate-600" />
            No automated emails tracked yet. Nudges, alerts and reports will show up here as they go out.
          </div>
        ) : (
          <div className="space-y-4">
            {stats.map((s) => (
              <div key={s.email_type} data-testid={`analytics-row-${s.email_type}`}>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="text-white">{LABELS[s.email_type] || s.email_type}</span>
                  <span className="font-mono text-xs text-slate-400">
                    {s.conversions}/{s.sent} converted · <span className="text-cyan-neon">{s.rate}%</span>
                  </span>
                </div>
                <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-cyan-neon to-[#39FF14] transition-all"
                    style={{ width: `${Math.max(s.rate, s.conversions > 0 ? 4 : 0)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};
